import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { tenantConfig } from '../config/tenant'
import Nav from '../components/Nav'
import Seo from '../components/Seo'
import Footer from '../components/Footer'
import LeadCaptureModal, { type LeadContext } from '../components/LeadCaptureModal'
import RatesStrip from '../components/RatesStrip'
import RangeField from '../components/RangeField'
import { formatNumberWithCommas, stripToNumber } from '../utils/formatCurrency'
import { estimateStampDuty, SRO_CALCULATORS, type AustralianState } from '../lib/stampDuty'
import {
  monthlyRepayment,
  extraRepaymentImpact,
  offsetImpact,
  formatMonths,
  type RepaymentType,
  type ExtraFrequency,
} from '../lib/repayment'
import { useRates } from '../hooks/useRates'
import { downloadResultsPdf, type ResultsPdfData } from '../lib/resultsPdf'

const AUD = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0,
})

/**
 * Rough LMI estimate. Real LMI comes from Genworth/QBE tables.
 * We just want the demo to produce a plausible non-zero number above 80% LVR.
 */
function estimateLmi(loanAmount: number, lvr: number): number {
  if (lvr <= 80) return 0
  const bandRate =
    lvr >= 95 ? 0.045 :
    lvr >= 90 ? 0.028 :
    lvr >= 85 ? 0.018 :
    0.010
  return Math.round(loanAmount * bandRate)
}

/**
 * Usable equity you may be able to access when refinancing. Lenders typically
 * lend up to ~80% of a property's value; usable equity is that ceiling minus
 * what you still owe. A guide only, a broker confirms it against your full
 * situation (LMI may let you go higher).
 */
function usableEquity(propertyValue: number, currentLoan: number, maxLvr = 80): number {
  return Math.max(0, Math.round(propertyValue * (maxLvr / 100) - currentLoan))
}

// Frequency toggle options for the extra-repayments section.
const FREQUENCIES: { value: ExtraFrequency; label: string; noun: string }[] = [
  { value: 'monthly', label: 'Monthly', noun: 'per month' },
  { value: 'fortnightly', label: 'Fortnightly', noun: 'per fortnight' },
  { value: 'weekly', label: 'Weekly', noun: 'per week' },
  { value: 'lumpSum', label: 'Lump sum', noun: 'one-off lump sum' },
]

export default function CalculatorPage() {
  // Repayment inputs
  const [loanAmount, setLoanAmount] = useState<number>(640_000)
  const [term, setTerm] = useState<number>(30)
  const [rate, setRate] = useState<number>(5.79)
  const [rateTouched, setRateTouched] = useState<boolean>(false)
  const [type, setType] = useState<RepaymentType>('PI')

  // Extra-repayments inputs
  const [extraAmount, setExtraAmount] = useState<number>(200)
  const [extraFrequency, setExtraFrequency] = useState<ExtraFrequency>('monthly')

  // Offset input
  const [offsetBalance, setOffsetBalance] = useState<number>(25_000)

  // Property-side inputs
  const [propertyPrice, setPropertyPrice] = useState<number>(800_000)
  const [deposit, setDeposit] = useState<number>(160_000)
  const [state, setState] = useState<AustralianState>('VIC')
  const [firstHome, setFirstHome] = useState<boolean>(true)

  // Refinance / usable-equity inputs
  const [equityValue, setEquityValue] = useState<number>(900_000)
  const [equityLoan, setEquityLoan] = useState<number>(500_000)
  const [equityLvr, setEquityLvr] = useState<number>(80)

  // Standalone savings comparison — current rate vs a new rate (its own inputs).
  const [svLoan, setSvLoan] = useState<number>(640_000)
  const [svTerm, setSvTerm] = useState<number>(30)
  const [svCurrentRate, setSvCurrentRate] = useState<number>(6.29)
  const [svNewRate, setSvNewRate] = useState<number>(5.79)

  const [leadOpen, setLeadOpen] = useState(false)
  const [leadMode, setLeadMode] = useState<'broker' | 'results'>('broker')

  // A2: seed the interest rate from today's LOWEST major-lender rate (instead of a
  // hardcoded 5.79), once the live feed loads and only while the user has not yet
  // edited it or picked a rate from the strip. Clicking a rate row still works.
  const { data: ratesData } = useRates()
  useEffect(() => {
    if (rateTouched) return
    const majors = ratesData?.major ?? []
    if (majors.length === 0) return
    const lowest = Math.min(...majors.map((r) => r.rate)) * 100
    if (Number.isFinite(lowest) && lowest > 0) setRate(Number(lowest.toFixed(2)))
  }, [ratesData, rateTouched])

  function pickRate(v: number) {
    setRateTouched(true)
    setRate(v)
  }

  const monthly = useMemo(() => monthlyRepayment(loanAmount, rate, term, type), [loanAmount, rate, term, type])
  const lvr = propertyPrice > 0 ? ((propertyPrice - deposit) / propertyPrice) * 100 : 0
  const impliedLoan = Math.max(0, propertyPrice - deposit)
  const stampDuty = useMemo(() => estimateStampDuty(propertyPrice, state, firstHome), [propertyPrice, state, firstHome])
  const lmi = useMemo(() => estimateLmi(impliedLoan, lvr), [impliedLoan, lvr])
  const equity = useMemo(() => usableEquity(equityValue, equityLoan, equityLvr), [equityValue, equityLoan, equityLvr])

  // Extra repayments + offset impact (both use the main loan / rate / term).
  const extra = useMemo(
    () => extraRepaymentImpact(loanAmount, rate, term, extraAmount, extraFrequency),
    [loanAmount, rate, term, extraAmount, extraFrequency],
  )
  const offset = useMemo(
    () => offsetImpact(loanAmount, rate, term, offsetBalance),
    [loanAmount, rate, term, offsetBalance],
  )

  const svMonthlySaving = useMemo(
    () => monthlyRepayment(svLoan, svCurrentRate, svTerm, 'PI') - monthlyRepayment(svLoan, svNewRate, svTerm, 'PI'),
    [svLoan, svCurrentRate, svNewRate, svTerm],
  )
  const svLifetimeSaving = svMonthlySaving * svTerm * 12

  // Auto-populate the loan amount from property price − deposit as the customer types.
  useEffect(() => {
    setLoanAmount(Math.max(0, propertyPrice - deposit))
  }, [propertyPrice, deposit])

  const isLump = extraFrequency === 'lumpSum'
  const extraMax = isLump ? 100_000 : 2_000
  const extraStep = isLump ? 1_000 : 50
  const freqNoun = FREQUENCIES.find((f) => f.value === extraFrequency)?.noun ?? ''

  function changeFrequency(f: ExtraFrequency) {
    setExtraFrequency(f)
    // Give each mode a sensible starting amount + keep it within the new slider range.
    if (f === 'lumpSum') setExtraAmount((a) => (a < 1_000 ? 20_000 : Math.min(a, 100_000)))
    else setExtraAmount((a) => (a > 2_000 ? 200 : a))
  }

  // Context carried into the lead + PDF for the "Get my results" capture.
  const resultsContext: LeadContext = {
    property_price: propertyPrice,
    deposit,
    loan_amount: loanAmount,
    lvr: Number(lvr.toFixed(1)),
    interest_rate: rate,
    term_years: term,
    state,
    first_home_buyer: firstHome,
    monthly_repayment: Math.round(monthly),
    stamp_duty: Math.round(stampDuty),
    lmi: Math.round(lmi),
    extra_repayment_amount: extraAmount || undefined,
    extra_repayment_frequency: extraAmount ? freqNoun : undefined,
    extra_interest_saved: extraAmount ? Math.round(extra.interestSaved) : undefined,
    extra_time_saved: extraAmount ? formatMonths(extra.monthsSaved) : undefined,
    offset_balance: offsetBalance || undefined,
    offset_interest_saved: offsetBalance ? Math.round(offset.interestSaved) : undefined,
    offset_time_saved: offsetBalance ? formatMonths(offset.monthsSaved) : undefined,
  }

  function handleResultsSubmitted(payload: { name: string; email: string; phone: string }) {
    const pdf: ResultsPdfData = {
      contact: { name: payload.name, email: payload.email, phone: payload.phone },
      repayment: {
        loanAmount,
        ratePercent: rate,
        termYears: term,
        type,
        monthly: Math.round(monthly),
      },
      extra:
        extraAmount > 0
          ? {
              amount: extraAmount,
              frequency: freqNoun,
              interestSaved: Math.round(extra.interestSaved),
              timeSavedLabel: formatMonths(extra.monthsSaved),
            }
          : undefined,
      offset:
        offsetBalance > 0
          ? {
              balance: offsetBalance,
              interestSaved: Math.round(offset.interestSaved),
              timeSavedLabel: formatMonths(offset.monthsSaved),
            }
          : undefined,
      property: {
        price: propertyPrice,
        deposit,
        state,
        lvr,
        stampDuty: Math.round(stampDuty),
        lmi: Math.round(lmi),
      },
    }
    downloadResultsPdf(pdf)
  }

  function openBroker() {
    setLeadMode('broker')
    setLeadOpen(true)
  }
  function openResults() {
    setLeadMode('results')
    setLeadOpen(true)
  }

  return (
    <>
      <Seo
        title="Home Loan Calculators"
        path="/tools"
        description="Free calculators for repayments, extra repayments, offset savings, stamp duty (all states), LMI and borrowing power, plus live lender rates."
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        <section className="max-w-7xl mx-auto px-6">
          <div className="mb-10">
            <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">Tools</div>
            <h1 className="font-heading text-3xl md:text-4xl text-ink">All-in-one calculator</h1>
            <p className="text-gs-text-soft mt-2 max-w-2xl">
              Work out your repayment, how much faster extra repayments pay off your loan, what an
              offset could save, plus stamp duty and LMI, all in one place. Numbers are indicative, a
              broker will confirm the real figures for your situation.
            </p>
          </div>

          <Link
            to="/tools/savings"
            className="block mb-10 rounded-2xl border border-[#E8E8E2] p-6 hover:shadow-card transition-shadow"
            style={{ background: '#F4FBD9' }}
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted">Refinance</div>
                <div className="font-heading text-xl text-ink mt-1">How much could you save?</div>
                <div className="text-gs-text-soft text-sm mt-1">Compare your current loan against a sharp rate from our lender panel.</div>
              </div>
              <span className="text-2xl text-ink shrink-0" aria-hidden="true">→</span>
            </div>
          </Link>

          {tenantConfig.propertyReportEnabled && (
            <Link
              to="/tools/property-report"
              className="block mb-10 bg-ink text-white rounded-2xl p-6 hover:opacity-95 transition-opacity"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: tenantConfig.accentColor }}>New · Free tool</div>
                  <div className="font-heading text-xl mt-1">Get a free property report</div>
                  <div className="text-white/70 text-sm mt-1">See advertised price history &amp; listing detail for any Australian address.</div>
                </div>
                <span className="text-2xl" aria-hidden="true">→</span>
              </div>
            </Link>
          )}

          <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
            {/* Left: calculator */}
            <div className="space-y-8">
              {/* Repayment */}
              <div className="bg-white border border-[#E8E8E2] rounded-2xl p-6">
                <h2 className="font-heading text-lg text-ink mb-4">Repayment</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <CurrencyField
                    id="calc-loan"
                    label="Loan amount"
                    value={loanAmount}
                    onChange={setLoanAmount}
                  />
                  <NumberField
                    id="calc-term"
                    label="Loan term (years)"
                    value={term}
                    onChange={setTerm}
                  />
                  <NumberField
                    id="calc-rate"
                    label="Interest rate (indicative)"
                    value={rate}
                    onChange={pickRate}
                    step={0.01}
                    suffix="%"
                  />
                  <div>
                    <span className="text-sm font-medium block mb-1">Repayment type</span>
                    <div className="flex gap-3 pt-1">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="rep-type"
                          checked={type === 'PI'}
                          onChange={() => setType('PI')}
                          aria-label="Principal and interest"
                        />
                        Principal & interest
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="rep-type"
                          checked={type === 'IO'}
                          onChange={() => setType('IO')}
                          aria-label="Interest only"
                        />
                        Interest only
                      </label>
                    </div>
                  </div>
                </div>
                <div
                  className="mt-6 p-6 rounded-xl flex items-center justify-between"
                  style={{ background: '#F4FBD9' }}
                >
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gs-text-soft">Monthly repayment (est.)</div>
                    <div
                      data-testid="monthly-repayment"
                      className="font-heading text-3xl text-ink tab-num mt-1"
                    >
                      {AUD.format(monthly)}
                    </div>
                  </div>
                  <button
                    onClick={openBroker}
                    className="px-5 py-3 rounded-lg font-semibold text-sm"
                    style={{ background: tenantConfig.primaryColor, color: tenantConfig.accentColor }}
                  >
                    Talk to a broker
                  </button>
                </div>
              </div>

              {/* Extra repayments — pay off faster */}
              <div className="bg-white border border-[#E8E8E2] rounded-2xl p-6">
                <h2 className="font-heading text-lg text-ink mb-1">Extra repayments, pay off faster</h2>
                <p className="text-sm text-gs-text-muted mb-5">
                  See how adding a little extra shortens your loan and cuts the interest you pay,
                  using the loan, rate and term above.
                </p>

                <div className="flex flex-wrap gap-2 mb-5">
                  {FREQUENCIES.map((f) => {
                    const activeF = extraFrequency === f.value
                    return (
                      <button
                        key={f.value}
                        type="button"
                        onClick={() => changeFrequency(f.value)}
                        aria-pressed={activeF}
                        className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                          activeF ? 'text-ink border-transparent' : 'text-gs-text-soft border-[#E8E8E2] hover:bg-[#FAFAF7]'
                        }`}
                        style={activeF ? { background: tenantConfig.accentColor } : undefined}
                      >
                        {f.label}
                      </button>
                    )
                  })}
                </div>

                <RangeField
                  id="extra-amount"
                  label={isLump ? 'One-off lump sum' : `Extra repayment (${freqNoun})`}
                  value={extraAmount}
                  onChange={setExtraAmount}
                  min={0}
                  max={extraMax}
                  step={extraStep}
                  format="currency"
                />

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div className="p-5 rounded-xl" style={{ background: '#F4FBD9' }}>
                    <div className="text-xs uppercase tracking-wider text-gs-text-soft">Interest saved</div>
                    <div data-testid="extra-interest-saved" className="font-heading text-2xl text-ink tab-num mt-1">
                      {AUD.format(Math.round(extra.interestSaved))}
                    </div>
                  </div>
                  <div className="p-5 rounded-xl border border-[#E8E8E2] bg-[#FAFAF7]">
                    <div className="text-xs uppercase tracking-wider text-gs-text-muted">Time saved</div>
                    <div data-testid="extra-time-saved" className="font-heading text-2xl text-ink mt-1">
                      {formatMonths(extra.monthsSaved)}
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-gs-text-muted">
                  <span>Without extra: {formatMonths(extra.baseMonths)}</span>
                  <span>With extra: {formatMonths(extra.newMonths)}</span>
                </div>
              </div>

              {/* Offset savings */}
              <div className="bg-white border border-[#E8E8E2] rounded-2xl p-6">
                <h2 className="font-heading text-lg text-ink mb-1">Offset savings</h2>
                <p className="text-sm text-gs-text-muted mb-5">
                  Money in an offset account reduces the balance you pay interest on, so your loan
                  clears sooner without changing your repayment.
                </p>

                <RangeField
                  id="offset-balance"
                  label="Offset account balance"
                  value={offsetBalance}
                  onChange={setOffsetBalance}
                  min={0}
                  max={200_000}
                  step={5_000}
                  format="currency"
                />

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div className="p-5 rounded-xl" style={{ background: '#F4FBD9' }}>
                    <div className="text-xs uppercase tracking-wider text-gs-text-soft">Interest saved</div>
                    <div data-testid="offset-interest-saved" className="font-heading text-2xl text-ink tab-num mt-1">
                      {AUD.format(Math.round(offset.interestSaved))}
                    </div>
                  </div>
                  <div className="p-5 rounded-xl border border-[#E8E8E2] bg-[#FAFAF7]">
                    <div className="text-xs uppercase tracking-wider text-gs-text-muted">Time saved</div>
                    <div data-testid="offset-time-saved" className="font-heading text-2xl text-ink mt-1">
                      {formatMonths(offset.monthsSaved)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Property costs */}
              <div className="bg-white border border-[#E8E8E2] rounded-2xl p-6">
                <h2 className="font-heading text-lg text-ink mb-4">Property costs</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <CurrencyField
                    id="calc-price"
                    label="Property price"
                    value={propertyPrice}
                    onChange={setPropertyPrice}
                  />
                  <CurrencyField
                    id="calc-deposit"
                    label="Deposit"
                    value={deposit}
                    onChange={setDeposit}
                  />
                  <div>
                    <label htmlFor="calc-state" className="block text-sm font-medium mb-1">State</label>
                    <select
                      id="calc-state"
                      value={state}
                      onChange={(e) => setState(e.target.value as AustralianState)}
                      className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm bg-white"
                    >
                      {(['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'] as AustralianState[]).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <span className="text-sm font-medium block mb-1">Buyer status</span>
                    <label className="flex items-center gap-2 text-sm pt-1">
                      <input
                        type="checkbox"
                        checked={firstHome}
                        onChange={(e) => setFirstHome(e.target.checked)}
                        aria-label="First home buyer"
                      />
                      First home buyer
                    </label>
                  </div>
                </div>
                <div className="mt-6 grid gap-4 md:grid-cols-3">
                  <StatCard label="LVR" value={`${lvr.toFixed(1)}%`} />
                  <StatCard label="Stamp duty (est.)" value={AUD.format(stampDuty)} testId="stamp-duty" />
                  <StatCard label="LMI (est.)" value={AUD.format(lmi)} testId="lmi-amount" />
                </div>
                <p className="mt-3 text-[11px] text-gs-text-muted leading-relaxed">
                  Stamp duty is an indicative {firstHome ? 'first-home ' : ''}estimate for {state}, the
                  exact figure depends on your full circumstances (concessions, surcharges, new vs
                  established).{' '}
                  <a
                    href={SRO_CALCULATORS[state]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:text-ink"
                  >
                    Confirm with the {state} revenue office →
                  </a>
                </p>
              </div>

              {/* Refinance / usable equity */}
              <div className="rounded-2xl border border-[#E8E8E2] bg-white p-6">
                <h2 className="font-heading text-xl text-ink mb-1">Usable equity</h2>
                <p className="text-sm text-gs-text-muted mb-5">
                  How much equity you may be able to access to refinance, renovate or invest.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <CurrencyField id="eq-value" label="Property value" value={equityValue} onChange={setEquityValue} />
                  <CurrencyField id="eq-loan" label="Current loan balance" value={equityLoan} onChange={setEquityLoan} />
                </div>
                <div className="mt-4">
                  <label htmlFor="eq-lvr" className="block text-sm font-medium mb-1">
                    Max lending level: {equityLvr}% of value
                  </label>
                  <input
                    id="eq-lvr"
                    type="range"
                    min={70}
                    max={90}
                    step={5}
                    value={equityLvr}
                    onChange={(e) => setEquityLvr(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gs-text-muted mt-1">
                    <span>70%</span><span>80% typical</span><span>90%</span>
                  </div>
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <StatCard label="Usable equity (est.)" value={AUD.format(equity)} testId="usable-equity" />
                  <div className="rounded-xl bg-[#FAFAF7] border border-[#E8E8E2] p-4 flex items-center">
                    <p className="text-xs text-gs-text-muted leading-relaxed">
                      Above 80% you may still access equity with LMI, a broker confirms your exact usable equity.
                    </p>
                  </div>
                </div>
              </div>

              {/* Get my results (PDF + lead capture) */}
              <div className="rounded-2xl bg-ink text-white p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="font-heading text-xl">Save your results as a PDF</h2>
                  <p className="text-white/70 text-sm mt-1 max-w-md">
                    Download a snapshot of your numbers to keep or share. One of our brokers will
                    follow up to help you make it real. No obligation.
                  </p>
                </div>
                <button
                  onClick={openResults}
                  className="shrink-0 px-6 py-3.5 rounded-xl font-semibold text-sm"
                  style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
                >
                  Get my results
                </button>
              </div>
            </div>

            {/* Right: rates strip. Clicking a rate row fills the repayment calculator's Interest-rate field. */}
            <div>
              <RatesStrip onPickRate={pickRate} />
              <div className="mt-6 p-6 rounded-2xl border border-[#E8E8E2] bg-white">
                <h3 className="font-heading text-lg text-ink mb-1">Why these numbers matter</h3>
                <p className="text-sm text-gs-text-soft mb-4">
                  Small rate changes stack up. See what switching from your current rate to a sharper one could save you.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <CurrencyField id="sv-loan" label="Loan balance" value={svLoan} onChange={setSvLoan} />
                  <NumberField id="sv-term" label="Remaining term (years)" value={svTerm} onChange={setSvTerm} step={1} />
                  <NumberField id="sv-current" label="Your current rate" value={svCurrentRate} onChange={setSvCurrentRate} step={0.01} suffix="%" />
                  <NumberField id="sv-new" label="New rate" value={svNewRate} onChange={setSvNewRate} step={0.01} suffix="%" />
                </div>
                <div className="mt-4 p-4 rounded-xl bg-[#F4FBD9] border border-[#E8E8E2]">
                  {svMonthlySaving > 0 ? (
                    <>
                      <div className="text-xs uppercase tracking-wider text-gs-text-muted mb-1">Your estimated saving</div>
                      <div className="font-heading text-2xl text-ink tab-num" data-testid="savings-monthly">
                        {AUD.format(Math.round(svMonthlySaving))}<span className="text-base text-gs-text-soft"> / month</span>
                      </div>
                      <div className="text-sm text-gs-text-soft mt-1" data-testid="savings-lifetime">
                        ≈ {AUD.format(Math.round(svLifetimeSaving))} over {svTerm} years
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gs-text-soft">
                      Enter a current rate higher than the new rate to see your potential saving.
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-gs-text-muted mt-3">
                  Indicative only, principal &amp; interest. A broker confirms the real figures for your situation.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <LeadCaptureModal
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        source="calculator"
        context={resultsContext}
        heading={leadMode === 'results' ? 'Get my results' : undefined}
        blurb={
          leadMode === 'results'
            ? 'Enter your details to download your PDF. A broker will follow up, no obligation.'
            : undefined
        }
        submitLabel={leadMode === 'results' ? 'Download my results (PDF)' : undefined}
        onSubmitted={leadMode === 'results' ? handleResultsSubmitted : undefined}
      />
    </>
  )
}

function NumberField({
  id, label, value, onChange, step = 1, prefix, suffix,
}: {
  id: string
  label: string
  value: number
  onChange: (v: number) => void
  step?: number
  prefix?: string
  suffix?: string
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-1">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute inset-y-0 left-3 flex items-center text-sm text-gs-text-muted">{prefix}</span>}
        <input
          id={id}
          type="number"
          step={step}
          value={Number.isFinite(value) ? value : ''}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className={`w-full py-2 border border-[#D8D8D0] rounded-lg text-sm tab-num ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-8' : 'pr-3'}`}
        />
        {suffix && <span className="absolute inset-y-0 right-3 flex items-center text-sm text-gs-text-muted">{suffix}</span>}
      </div>
    </div>
  )
}

/**
 * Currency input — shows comma separators as you type (1200000 → 1,200,000),
 * sends the raw number to the parent. Holds its own text state so the field is
 * always clearable (empty stays empty, never a stuck "0") and never keeps a
 * leading zero. Ported from LoanLoop's string-state currency pattern.
 */
function CurrencyField({
  id, label, value, onChange,
}: {
  id: string
  label: string
  value: number
  onChange: (v: number) => void
}) {
  const [text, setText] = useState(value ? formatNumberWithCommas(String(value)) : '')

  // Re-sync when the parent value changes from OUTSIDE this input (e.g. loan
  // amount auto-populating from price − deposit) — but not while the user is
  // mid-edit (guarded by comparing the numeric values).
  useEffect(() => {
    const shown = Number(stripToNumber(text)) || 0
    if (shown !== value) {
      setText(value ? formatNumberWithCommas(String(value)) : '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    // strip to digits, drop any leading zeros, then re-format
    const raw = stripToNumber(e.target.value).replace(/^0+(?=\d)/, '')
    setText(raw ? formatNumberWithCommas(raw) : '')
    onChange(Number(raw) || 0)
  }

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium mb-1">{label}</label>
      <div className="relative">
        <span className="absolute inset-y-0 left-3 flex items-center text-sm text-gs-text-muted">$</span>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          value={text}
          onChange={handleChange}
          placeholder="0"
          className="w-full py-2 pl-7 pr-3 border border-[#D8D8D0] rounded-lg text-sm tab-num"
        />
      </div>
    </div>
  )
}

function StatCard({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="p-4 rounded-xl border border-[#E8E8E2] bg-[#FAFAF7]">
      <div className="text-xs uppercase tracking-wider text-gs-text-muted">{label}</div>
      <div data-testid={testId} className="font-heading text-xl text-ink mt-1 tab-num">{value}</div>
    </div>
  )
}
